import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment';

export interface CourseSettings {
  classCooldownPeriod: {
    days: number;
    hours: number;
    minutes: number;
  };
  classManual: string;
  classFAQ: string;
  classDocuments: {
    [key: string]: {
      documentUrl: string;
    };
  };
}

export interface ClassDocument {
  documentUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  private courseSettingsSubject = new BehaviorSubject<CourseSettings | null>(null);
  public courseSettings$ = this.courseSettingsSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Load course settings from backend, fallback to environment
   */
  async loadCourseSettings(): Promise<CourseSettings> {
    try {
      const backendSettings = await this.http.get<CourseSettings>(`${environment.baseUrl}configuration/course-settings`).toPromise();
      if (backendSettings) {
        this.courseSettingsSubject.next(backendSettings);
        return backendSettings;
      }
    } catch (error) {
      console.warn('Failed to load backend configuration, using environment defaults:', error);
    }

    // Fallback to environment settings
    const fallbackSettings: CourseSettings = {
      classCooldownPeriod: environment.courseSettings.classCooldownPeriod,
      classManual: environment.classManual || '',
      classFAQ: environment.classFAQ || '',
      classDocuments: environment.classDocuments || {}
    };
    
    this.courseSettingsSubject.next(fallbackSettings);
    return fallbackSettings;
  }

  /**
   * Get current course settings (sync)
   */
  getCurrentCourseSettings(): CourseSettings {
    const current = this.courseSettingsSubject.getValue();
    console.log('Current Course Settings:', current);
    if (current) {
      return current;
    }
    console.log('No current course settings found, using environment defaults.');
    // Return environment defaults if not loaded yet
    return {
      classCooldownPeriod: environment.courseSettings.classCooldownPeriod,
      classManual: environment.classManual || '',
      classFAQ: environment.classFAQ || '',
      classDocuments: environment.classDocuments || {}
    };
  }

  /**
   * Calculate total cooldown period in minutes
   */
  calculateCooldownMinutes(): number {
    const settings = this.getCurrentCourseSettings();
    const cooldownConfig = settings.classCooldownPeriod;
    
    const totalMinutes = 
      (cooldownConfig.days || 0) * 24 * 60 +     // Convert days to minutes
      (cooldownConfig.hours || 0) * 60 +         // Convert hours to minutes  
      (cooldownConfig.minutes || 0);             // Direct minutes
      
    return totalMinutes;
  }

  /**
   * Generate fallback URL pointing to API base for error tracking
   */
  private generateFallbackUrl(type: 'manual' | 'document', classNumber?: number): string {
    const baseUrl = environment.baseUrl.replace('/api/', '');
    const path = type === 'manual' 
      ? 'configuration/class-manual-error' 
      : `configuration/class-document-error/${classNumber}`;
    return `${baseUrl}${path}`;
  }

  /**
   * Get class manual URL with API base fallback for error tracking
   */
  getClassManualUrl(): string {
    const settings = this.getCurrentCourseSettings();
    
    // Try API settings first
    if (settings.classManual && this.isValidUrl(settings.classManual)) {
      return settings.classManual;
    }
    
    // Try environment fallback
    if (environment.classManual && this.isValidUrl(environment.classManual)) {
      return environment.classManual;
    }
    
    // Return API base fallback for error tracking
    console.warn('⚠️ Class manual URL not found or invalid, using API fallback for tracking');
    return this.generateFallbackUrl('manual');
  }

    /**
   * Get class manual URL with API base fallback for error tracking
   */
  getClassFAQ_URL(): string {
    const settings = this.getCurrentCourseSettings();
    
    // Try API settings first
    if (settings.classFAQ && this.isValidUrl(settings.classFAQ)) {
      return settings.classFAQ;
    }
    
    // Try environment fallback
    if (environment.classFAQ && this.isValidUrl(environment.classFAQ)) {
      return environment.classFAQ;
    }
    
    // Return API base fallback for error tracking
    console.warn('⚠️ Class manual URL not found or invalid, using API fallback for tracking');
    return this.generateFallbackUrl('manual');
  }


  /**
   * Validate if a URL is properly formatted
   */
  private isValidUrl(url: string): boolean {
    if (!url || url.trim() === '') return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get document URL for a specific class with API base fallback for error tracking
   * @param classNumber - Class number (1-10)
   */
  getClassDocumentUrl(classNumber: number): string {
    const settings = this.getCurrentCourseSettings();
    const classKey = `Class${classNumber}`;
    
    // Validate class number
    if (classNumber < 1 || classNumber > 10) {
      console.warn(`⚠️ Invalid class number: ${classNumber}. Must be 1-10.`);
      return this.generateFallbackUrl('document', classNumber);
    }
    
    // Try API settings first
    if (settings.classDocuments && settings.classDocuments[classKey]) {
      const url = settings.classDocuments[classKey].documentUrl;
      if (this.isValidUrl(url)) {
        return url;
      }
      console.warn(`⚠️ Invalid document URL for ${classKey} from API: ${url}`);
    }
    
    // Try environment fallback
    if (environment.classDocuments && environment.classDocuments[classKey]) {
      const url = environment.classDocuments[classKey].documentUrl;
      if (this.isValidUrl(url)) {
        return url;
      }
      console.warn(`⚠️ Invalid document URL for ${classKey} from environment: ${url}`);
    }
    
    // Return API base fallback for error tracking
    console.warn(`⚠️ Document URL for ${classKey} not found or invalid, using API fallback for tracking`);
    return this.generateFallbackUrl('document', classNumber);
  }

  /**
   * Get all class documents
   */
  getAllClassDocuments(): { [key: string]: ClassDocument } {
    const settings = this.getCurrentCourseSettings();
    return settings.classDocuments || environment.classDocuments || {};
  }

  /**
   * Get configuration health status for debugging
   */
  getConfigurationHealth(): {
    apiLoaded: boolean;
    classManualValid: boolean;
    documentsStatus: { [key: string]: boolean };
    fallbacksUsed: string[];
    errors: string[];
  } {
    const settings = this.getCurrentCourseSettings();
    const health = {
      apiLoaded: this.courseSettingsSubject.getValue() !== null,
      classManualValid: false,
      documentsStatus: {} as { [key: string]: boolean },
      fallbacksUsed: [] as string[],
      errors: [] as string[]
    };

    // Check class manual
    if (settings.classManual && this.isValidUrl(settings.classManual)) {
      health.classManualValid = true;
    } else {
      health.fallbacksUsed.push('classManual');
      if (environment.classManual && this.isValidUrl(environment.classManual)) {
        health.classManualValid = true;
      } else {
        health.errors.push('Class manual URL invalid or missing');
      }
    }

    // Check each class document
    for (let i = 1; i <= 10; i++) {
      const classKey = `Class${i}`;
      const apiUrl = settings.classDocuments?.[classKey]?.documentUrl;
      const envUrl = environment.classDocuments?.[classKey]?.documentUrl;
      
      if (apiUrl && this.isValidUrl(apiUrl)) {
        health.documentsStatus[classKey] = true;
      } else if (envUrl && this.isValidUrl(envUrl)) {
        health.documentsStatus[classKey] = true;
        health.fallbacksUsed.push(classKey);
      } else {
        health.documentsStatus[classKey] = false;
        health.errors.push(`${classKey} document URL invalid or missing`);
      }
    }

    return health;
  }

  /**
   * Log configuration health status to console for debugging
   */
  logConfigurationHealth(): void {
    const health = this.getConfigurationHealth();
    console.group('🔧 Configuration Health Status');
    console.log('📊 API Loaded:', health.apiLoaded);
    console.log('📋 Class Manual Valid:', health.classManualValid);
    console.log('📚 Documents Status:', health.documentsStatus);
    
    if (health.fallbacksUsed.length > 0) {
      console.warn('⚠️ Fallbacks Used:', health.fallbacksUsed);
    }
    
    if (health.errors.length > 0) {
      console.error('❌ Errors Found:', health.errors);
    }
    
    console.groupEnd();
  }

  get totalClasses(): number {
    return environment.courseStructure.totalClasses || 10;
  }
  
  get activitiesPerClass(): number {
    return environment.courseStructure.activitiesPerClass || 3;
  }
  
  get totalActivities(): number {
    return environment.courseStructure.totalActivities || this.totalClasses * this.activitiesPerClass;
  }
  
  calculateProgress(completedActivities: number): number {
    return Math.round((completedActivities / this.totalActivities) * 100);
  }
  
  isCourseComplete(completedActivities: number): boolean {
    console.log(`Checking course completion: ${completedActivities} completed out of ${this.totalActivities} total activities.`, `Progress: ${this.calculateProgress(completedActivities)}%`);
    return completedActivities >= this.totalActivities;
  }

}
